/**
 * The Mermaid renderer (FRIDAY-67).
 *
 * Mermaid is a big dependency and most pages have no diagram at all, so
 * it is loaded with a dynamic `import()` — the only place it enters the
 * bundle. The chunk is fetched the first time a diagram is rendered and
 * never again.
 *
 * Rules this module keeps:
 *   - Lazy: no diagram, no chunk.
 *   - Initialised once per theme, with `securityLevel: 'strict'` (no raw
 *     HTML in labels, no click callbacks) and `startOnLoad: false` (we
 *     render by hand).
 *   - Serialised: Mermaid writes into the DOM while it renders, so two
 *     diagrams are never rendered at the same time.
 *   - Unique ids: every render gets its own id, so two diagrams on one
 *     page never collide.
 */

import { MAX_EDGES, MAX_SOURCE_LENGTH, checkMermaidSource } from './limits'
import type { MermaidTheme } from './theme'

type MermaidApi = (typeof import('mermaid'))['default']

/** The error we show when a diagram cannot be rendered. */
export class MermaidRenderError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'MermaidRenderError'
  }
}

let mermaidPromise: Promise<MermaidApi> | null = null
let configuredThemeId: string | null = null
let renderCounter = 0

/** Renders are chained: Mermaid mutates the DOM while it draws. */
let queue: Promise<unknown> = Promise.resolve()

async function loadMermaid(): Promise<MermaidApi> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid')
      .then((mod) => mod.default)
      .catch((error: unknown) => {
        // Let the next attempt try again — the chunk may have been a
        // temporary network failure.
        mermaidPromise = null
        throw new MermaidRenderError(`The diagram renderer could not be loaded (${describeError(error)}).`)
      })
  }
  return mermaidPromise
}

async function readyMermaid(theme: MermaidTheme): Promise<MermaidApi> {
  const mermaid = await loadMermaid()
  if (configuredThemeId !== theme.id) {
    mermaid.initialize({
      // We render by hand, never on page load.
      startOnLoad: false,
      // No raw HTML in labels, no click callbacks, no scripts.
      securityLevel: 'strict',
      // We draw our own error state instead of Mermaid's error diagram.
      suppressErrorRendering: true,
      // Ids inside the SVG must not repeat between two diagrams.
      deterministicIds: false,
      maxTextSize: MAX_SOURCE_LENGTH,
      maxEdges: MAX_EDGES,
      theme: theme.dark ? 'dark' : 'default',
      themeVariables: theme.variables,
    })
    configuredThemeId = theme.id
  }
  return mermaid
}

/**
 * Renders a diagram and returns its SVG markup.
 *
 * The markup comes from Mermaid in `strict` mode, where it is passed
 * through DOMPurify. It is inserted into the page as a parsed SVG node —
 * never as raw HTML.
 */
export function renderMermaid(source: string, theme: MermaidTheme): Promise<string> {
  const task = queue.then(async () => {
    const check = checkMermaidSource(source)
    if (!check.ok) throw new MermaidRenderError(check.message)

    const mermaid = await readyMermaid(theme)
    renderCounter += 1
    const id = `friday-mermaid-${renderCounter}`
    try {
      const { svg } = await mermaid.render(id, source)
      return svg
    } catch (error) {
      throw new MermaidRenderError(describeError(error))
    }
  })
  // Keep the queue alive even when one render fails.
  queue = task.catch(() => undefined)
  return task
}

/** Turns Mermaid's error into one readable line. */
function describeError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error)
  const firstLine =
    raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .find((line) => !line.startsWith('mermaid version')) ?? ''
  const message = firstLine || 'The diagram could not be rendered.'
  return message.length > 400 ? `${message.slice(0, 400)}…` : message
}

/**
 * Parses Mermaid's SVG markup into an SVG element.
 *
 * The HTML parser puts `<svg>` in the SVG namespace, so the node can be
 * imported into the document as-is. Nothing is executed: the markup is
 * parsed, never assigned to `innerHTML`.
 */
export function svgElementFrom(markup: string): Element {
  const parsed = new DOMParser().parseFromString(`<div>${markup}</div>`, 'text/html')
  const svg = parsed.body.firstElementChild?.firstElementChild
  if (!svg || svg.tagName.toLowerCase() !== 'svg') {
    throw new MermaidRenderError('The diagram could not be rendered.')
  }
  return document.importNode(svg, true)
}
